import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Target, Users, Zap, Shield, Cpu, Globe, TrendingUp } from 'lucide-react';
import { SmoothScroll } from '../components/SmoothScroll';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { mockData } from '../mock';

const values = [
  {
    Icon: Target,
    title: 'Purpose-driven',
    description: 'Every engagement starts with your operational reality — not a template. We design for the exact plant, network, and threat landscape you live in.'
  },
  {
    Icon: Users,
    title: 'Partnership over vendor',
    description: 'We stay long after the report is delivered. Our engineers become an extension of your internal team, on call when incidents escalate.'
  },
  {
    Icon: Zap,
    title: 'Safety before uptime',
    description: 'Operational technology protects lives before it protects margins. Every control we deploy passes that filter first.'
  }
];

const capabilities = [
  { Icon: Shield, title: 'OT Cybersecurity', description: 'Threat modeling, segmentation, IDS/IPS deployment, and incident response for ICS/SCADA environments.' },
  { Icon: Cpu, title: 'Automation Engineering', description: 'End-to-end PLC, DCS, and HMI design, programming, migration and commissioning across process industries.' },
  { Icon: Globe, title: 'Global Delivery', description: 'Teams working across 6 countries with regional expertise in Middle East, South Asia, Europe and North America.' },
  { Icon: TrendingUp, title: 'Continuous Optimization', description: 'Post-deployment monitoring, KPI dashboards and iterative tuning to keep systems performing at peak.' }
];

const AboutPage = () => {
  const [statsRef, statsIn] = useInView({ threshold: 0.2, triggerOnce: true });
  const [valuesRef, valuesIn] = useInView({ threshold: 0.1, triggerOnce: true });
  const [capRef, capIn] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <SmoothScroll>
      <div className="relative bg-slate-50">
        <Header />

        {/* Hero band */}
        <section className="relative pt-40 pb-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.12 }}
            transition={{ duration: 1.5 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-500 rounded-full blur-[150px]"
          />

          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <Link
                to="/"
                data-testid="about-back-to-home"
                className="inline-flex items-center gap-2 text-slate-300 hover:text-amber-400 transition-colors duration-300 group"
              >
                <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
                <span className="text-sm tracking-wide uppercase">Back to Home</span>
              </Link>
            </motion.div>

            <div className="max-w-4xl">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-amber-400 text-sm tracking-[0.3em] uppercase mb-6"
              >
                About OT Resilience
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.4, 0.25, 1] }}
                className="text-5xl md:text-7xl font-bold leading-tight mb-8"
              >
                <span className="bg-gradient-to-r from-white via-amber-50 to-amber-400 bg-clip-text text-transparent">
                  Engineering resilience into critical infrastructure.
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-slate-300 max-w-3xl font-light leading-relaxed"
              >
                We are an operational technology engineering and cybersecurity consultancy protecting the systems the modern world runs on — power grids, water utilities, manufacturing floors, transportation networks and the industrial cloud that stitches them together.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Who we are — long-form */}
        <section className="relative py-24 md:py-32 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-amber-600 text-sm tracking-[0.3em] uppercase mb-4"
            >
              Who we are
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-slate-950 leading-tight mb-10"
            >
              A different kind of consultancy — built by engineers, not slide-deck consultants.
            </motion.h2>

            <div className="space-y-6 text-lg text-slate-700 leading-relaxed">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                OT Resilience was founded on a simple, uncompromising principle: the systems that keep hospitals lit, water flowing, factories running and grids stable deserve security that understands how they actually work. Not IT security bolted on. Not compliance theatre. Real operational-first engineering.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Our team combines 26 years of collective experience across process control, industrial automation, network architecture and offensive security. We have designed control systems from a blank page, hardened brownfield ICS deployments under regulator pressure, and led incident response after real intrusions into safety-critical environments. That mix — build, break, defend — is rare in a consultancy, and it is the foundation of every engagement we take.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                Headquartered in Ajman, UAE, we serve clients across 6 countries in critical sectors including oil & gas, water & wastewater, power generation, manufacturing, and transportation. From the initial risk assessment to the last commissioning walkthrough — and every audit, upgrade and incident response in between — we operate as a long-term partner, not a transactional vendor.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Mission / Vision cards */}
        <section className="relative py-20 bg-slate-950 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-10" />
          <div className="relative z-10 max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="p-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl"
            >
              <div className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-4">Our Mission</div>
              <h3 className="text-3xl font-bold text-white mb-4">Secure. Sustain. Automate.</h3>
              <p className="text-slate-300 leading-relaxed">
                To engineer measurably safer, more reliable and more autonomous industrial operations — protecting the physical processes that societies depend on from cyber, human and systemic failure.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="p-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl"
            >
              <div className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-4">Our Vision</div>
              <h3 className="text-3xl font-bold text-white mb-4">Zero-incident industrial infrastructure.</h3>
              <p className="text-slate-300 leading-relaxed">
                A future where every critical facility runs on hardened, monitored, self-healing OT — where breaches become non-events and downtime is measured in minutes per decade, not hours per year.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats strip */}
        <section ref={statsRef} className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {mockData.hero.stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={statsIn ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="p-8 bg-white border border-slate-200 rounded-2xl text-center shadow-sm hover:shadow-lg hover:border-amber-300 transition-all duration-300"
              >
                <div className="text-5xl font-bold text-amber-500 mb-3">{s.number}</div>
                <div className="text-xs tracking-wider uppercase text-slate-500">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section ref={valuesRef} className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={valuesIn ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-amber-600 text-sm tracking-[0.3em] uppercase mb-4 text-center"
            >
              What we believe
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={valuesIn ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-slate-950 text-center mb-16"
            >
              Three principles that never bend.
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-6">
              {values.map(({ Icon, title, description }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={valuesIn ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  whileHover={{ y: -4 }}
                  className="p-8 bg-slate-50 border border-slate-200 rounded-3xl hover:border-amber-400 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-slate-950" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-950 mb-3">{title}</h3>
                  <p className="text-slate-600 leading-relaxed">{description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What we do — capabilities */}
        <section ref={capRef} className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={capIn ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-amber-600 text-sm tracking-[0.3em] uppercase mb-4"
            >
              What we do
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={capIn ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-slate-950 mb-16 max-w-3xl"
            >
              A full-lifecycle OT partner — from blueprint to breach response.
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {capabilities.map(({ Icon, title, description }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={capIn ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="p-6 bg-white border border-slate-200 rounded-2xl h-full hover:border-amber-400 hover:shadow-lg transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-amber-500 mb-4" strokeWidth={1.75} />
                  <h4 className="text-lg font-bold text-slate-950 mb-2">{title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold text-slate-950 mb-6"
            >
              Ready to design your resilience program?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto"
            >
              Get a no-obligation scoping conversation with a senior OT engineer within 24 hours.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link
                to="/contact#contact"
                data-testid="about-cta-contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-semibold rounded-full shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-105 transition-all duration-300"
              >
                <span>Talk to an engineer</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </SmoothScroll>
  );
};

export default AboutPage;
